// client/src/pages/Landing.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LoginCredentials } from '../types/auth';
import WebAuthnLogin from '../components/WebAuthn/WebAuthnLogin';
import TOTPLogin from '../components/TOTP/TOTPLogin';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading: authLoading, updateToken, isAuthenticated, user } = useAuth();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{email?: string; password?: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUserId, setMfaUserId] = useState<string | null>(null);
  // AUTH-12: post-password MFA challenge token (replaces bare userId)
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaType, setMfaType] = useState<'webauthn' | 'totp'>('webauthn');
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.requirePasswordChange) {
        navigate('/change-password');
      } else if (user.role === 'counsellor') {
        const dashboardStatuses = ['Active', 'Pending Agreement', 'Pending Self-Completion'];
        if (dashboardStatuses.includes(user.applicationStatus)) {
          navigate('/counsellor/dashboard');
        } else {
          navigate('/counsellor/onboarding');
        }
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Validate form inputs
  const validateForm = (): boolean => {
    const errors: {email?: string; password?: string} = {};
    
    if (!credentials.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!credentials.password) {
      errors.password = 'Password is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission with enhanced error prevention
  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    // Ensure the default form submission is prevented
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent any possible form submission
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    
    setError(null);
    setValidationErrors({});
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
  
    try {
      // Make direct API call to handle MFA response
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Check if MFA setup is required for existing users
      if (data.requiresMFASetup && data.setupToken) {
        setMfaSetupRequired(true);
        setSetupToken(data.setupToken);
        setMfaUserId(data.userId);
        setError(null);
        return;
      }

      // Check if MFA is required (all users now require MFA)
      if (data.requiresMFA) {
        setMfaRequired(true);
        setMfaToken(data.mfaToken);
        setMfaType(data.mfaType || 'webauthn'); // Set the MFA type from backend
        setError(null);
        return;
      }

      // This should not happen anymore since all users require MFA
      console.warn('User logged in without MFA - this should not happen');
      await login(credentials.email, credentials.password);
      // Navigation is handled by the auth context on success
    } catch (error: any) {
      console.error('Login error:', error);
      // Extract error message from various possible sources
      const errorMessage = 
        error.message || 
        'Invalid email or password. Please try again.';
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle WebAuthn MFA completion
  const handleMfaComplete = async (credentialResponse: any) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('/api/auth/login/mfa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mfaToken,
          credentialResponse,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'MFA verification failed');
      }

      // Use updateToken to store token and trigger verification
      // This will automatically verify the token and update auth state
      updateToken(data.token);
      // Navigation will be handled by useEffect when isAuthenticated becomes true
    } catch (error: any) {
      console.error('MFA completion error:', error);
      setError(error.message || 'Multi-factor authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle TOTP MFA completion
  const handleTotpComplete = async (totpCode: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('/api/auth/login/totp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mfaToken,
          token: totpCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'TOTP verification failed');
      }

      // Use updateToken to store token and trigger verification
      updateToken(data.token);
      // Navigation will be handled by useEffect when isAuthenticated becomes true
    } catch (error: any) {
      console.error('TOTP completion error:', error);
      setError(error.message || 'Verification code invalid');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle MFA setup redirect for existing users
  const handleMfaSetupRedirect = () => {
    if (setupToken && mfaUserId) {
      // Store the setup token and redirect to MFA setup
      localStorage.setItem('mfaSetupToken', setupToken);
      navigate('/mfa-setup', { 
        state: { 
          fromLogin: true,
          userId: mfaUserId,
          message: 'Complete your MFA setup to access your account' 
        }
      });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-indigo-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-indigo-950 flex flex-col justify-between overflow-hidden relative">
      {/* Enhanced background with more depth */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-600 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute top-40 -left-20 w-80 h-80 bg-indigo-500 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-[30rem] h-[30rem] bg-blue-700 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-40 left-1/4 w-64 h-64 bg-purple-600 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-indigo-400 rounded-full opacity-10 blur-3xl"></div>
      </div>
      
      <div className="w-full max-w-md mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-4 flex flex-col items-center">
        {/* Header text */}
        <div className="text-center mb-1"> 
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 drop-shadow-md tracking-wider sm:tracking-[0.2em] md:tracking-[0.3em]">Standard Practice</h1>
          <p className="text-sm sm:text-base md:text-lg text-white tracking-wide max-w-xs mx-auto">Streamline your practice with our comprehensive client management application</p>
        </div>
        
        {/* Logo image - now below text and larger */}
        <div className="flex justify-center mb-3"> {/* Minimal gap between image and sign-in form */}
          <img 
            src={`${process.env.PUBLIC_URL}/logo.png`} 
            alt="Standard Practice Logo" 
            className="h-40 sm:h-60 md:h-80 w-auto" 
          />
        </div>

        {/* Login Form - Enhanced */}
        <div className="w-full">
          <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-2xl p-4 sm:p-6 md:p-8 border border-white/10">
            
            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-6 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Show MFA setup requirement for existing users */}
            {mfaSetupRequired ? (
              <div className="text-center">
                <div className="mb-6">
                  <svg className="h-12 w-12 text-amber-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="text-lg font-medium text-white mb-2">Security Setup Required</h3>
                  <p className="text-blue-100 text-sm mb-4">
                    To enhance security, all accounts now require multi-factor authentication. 
                    Please complete your MFA setup to continue.
                  </p>
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-6">
                    <p className="text-amber-200 text-sm">
                      <strong>What is MFA?</strong> Multi-factor authentication adds an extra layer of security 
                      using your fingerprint, face recognition, or a security key.
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={handleMfaSetupRedirect}
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Setting up...
                    </>
                  ) : (
                    'Set Up Multi-Factor Authentication'
                  )}
                </button>
                
                <button
                  onClick={() => {
                    setMfaSetupRequired(false);
                    setSetupToken(null);
                    setMfaUserId(null);
                    setError(null);
                  }}
                  className="text-sm text-blue-300 hover:text-blue-200 underline"
                >
                  ← Back to login
                </button>
              </div>
            ) : mfaRequired ? (
              <div className="text-center">
                <div className="mb-6">
                  <svg className="h-12 w-12 text-blue-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <h3 className="text-lg font-medium text-white mb-2">Multi-Factor Authentication Required</h3>
                  <p className="text-blue-100 text-sm mb-4">
                    {mfaType === 'totp' 
                      ? 'Enter the verification code from your authenticator app.'
                      : 'Please use your biometric authentication or security key to complete sign in.'}
                  </p>
                </div>
                
                {mfaType === 'totp' ? (
                  <TOTPLogin
                    onSuccess={handleTotpComplete}
                    onError={(error) => setError(error)}
                    disabled={isLoading}
                  />
                ) : (
                  <WebAuthnLogin
                    onSuccess={handleMfaComplete}
                    onError={(error) => setError(error)}
                    disabled={isLoading}
                    mfaMode={true}
                    mfaToken={mfaToken || undefined}
                  />
                )}
                
                <button
                  onClick={() => {
                    setMfaRequired(false);
                    setMfaUserId(null);
                    setMfaToken(null);
                    setError(null);
                  }}
                  className="mt-4 text-sm text-blue-300 hover:text-blue-200 underline"
                >
                  ← Back to login
                </button>
              </div>
            ) : (

            <form className="space-y-4 sm:space-y-6" onSubmit={handleLogin} noValidate>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-blue-100">
                  Email address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-blue-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={credentials.email}
                    onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                    className="block w-full pl-10 pr-3 py-2 sm:py-3 border border-white/20 rounded-md leading-5 bg-white/5 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    placeholder="you@example.com"
                    disabled={isLoading}
                  />
                </div>
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-400" role="alert">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-blue-100">
                  Password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-blue-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="block w-full pl-10 pr-10 py-2 sm:py-3 border border-white/20 rounded-md leading-5 bg-white/5 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-blue-300 hover:text-blue-200 focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="mt-1 text-sm text-red-400" role="alert">{validationErrors.password}</p>
                )}
              </div>

              <div className="flex items-center mb-2 sm:mb-4">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-white/20 rounded bg-white/5"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-blue-100">
                  Remember me
                </label>
              </div>

              <div className="text-sm text-center mb-4">
                <Link to="/forgot-password" className="font-medium text-indigo-300 hover:text-indigo-200">
                  Forgot your password?
                </Link>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 sm:py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="w-full py-4 text-center text-sm text-blue-200/60 mt-auto">
        &copy; {new Date().getFullYear()} Standard Practice. All rights reserved.
      </div>
    </div>
  );
};

export default Landing;